import type { SettingsData } from './persistence';
import type { Sim } from '../sim/sim';
import type { PlayerId } from '../content/types';
import { dist } from '../shared/vec';

export type MusicScene = 'menu' | 'match';

type MusicTrack = 'menu' | 'ingame' | 'baseAttack';

interface MusicDeck {
  element: HTMLAudioElement;
  source: MediaElementAudioSourceNode;
  gain: GainNode;
}

const TRACK_URLS: Record<MusicTrack, string> = {
  menu: '/assets/audio/music/menu.mp3',
  ingame: '/assets/audio/music/ingame.mp3',
  baseAttack: '/assets/audio/music/base-attack.mp3',
};

const CROSSFADE_SECONDS = 2.8;
const BASE_ATTACK_RELEASE_MS = 10_000;
const CROSSFADE_STEPS = 64;
const MUSIC_MIX = 0.65;

/**
 * Three-deck music mixer. All tracks stay rolling silently after the first user gesture so
 * returning from the attack score is seamless rather than restarting the normal match track.
 */
export class MusicHandler {
  private context: AudioContext | null = null;
  private output: GainNode | null = null;
  private decks = new Map<MusicTrack, MusicDeck>();
  private scene: MusicScene = 'menu';
  private current: MusicTrack | null = null;
  private baseThreatActive = false;
  private releaseTimer: number | null = null;
  private disposed = false;
  private settings: SettingsData;

  constructor(settings: SettingsData) {
    this.settings = settings;
  }

  attach(context: AudioContext): void {
    if (this.disposed || this.context) return;
    this.context = context;
    this.output = context.createGain();
    this.output.connect(context.destination);

    for (const [track, url] of Object.entries(TRACK_URLS) as [MusicTrack, string][]) {
      const element = new Audio(url);
      element.loop = true;
      element.preload = 'auto';
      const source = context.createMediaElementSource(element);
      const gain = context.createGain();
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(this.output);
      this.decks.set(track, { element, source, gain });
      // attach() runs inside the first pointer/key gesture. Starting every deck here avoids
      // later autoplay rejections when combat begins without another interaction.
      void element.play().catch(() => undefined);
    }

    this.updateVolume();
    this.transitionTo(this.scene === 'menu' ? 'menu' : 'ingame');
  }

  setSettings(settings: SettingsData): void {
    this.settings = settings;
    this.updateVolume();
  }

  setScene(scene: MusicScene): void {
    if (this.scene === scene) return;
    this.scene = scene;
    if (scene === 'menu') {
      this.baseThreatActive = false;
      this.clearReleaseTimer();
      this.transitionTo('menu');
    } else {
      this.transitionTo(this.baseThreatActive ? 'baseAttack' : 'ingame');
    }
  }

  setBaseThreat(active: boolean): void {
    if (active) {
      this.baseThreatActive = true;
      this.clearReleaseTimer();
      if (this.scene === 'match') this.transitionTo('baseAttack');
      return;
    }

    if (!this.baseThreatActive || this.releaseTimer !== null) return;
    this.releaseTimer = window.setTimeout(() => {
      this.releaseTimer = null;
      this.baseThreatActive = false;
      if (this.scene === 'match') this.transitionTo('ingame');
    }, BASE_ATTACK_RELEASE_MS);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.clearReleaseTimer();
    for (const deck of this.decks.values()) {
      deck.element.pause();
      deck.source.disconnect();
      deck.gain.disconnect();
    }
    this.decks.clear();
    this.output?.disconnect();
    this.output = null;
    this.context = null;
  }

  private updateVolume(): void {
    if (!this.context || !this.output) return;
    const volume = (this.settings.masterVol / 100) * (this.settings.musicVol / 100) * MUSIC_MIX;
    this.output.gain.setTargetAtTime(volume, this.context.currentTime, 0.03);
  }

  private transitionTo(target: MusicTrack): void {
    if (!this.context || this.current === target) return;
    const now = this.context.currentTime;
    for (const [track, deck] of this.decks) {
      const parameter = deck.gain.gain;
      const start = parameter.value;
      // A match can be entered/exited faster than one crossfade. Hold the exact live value and
      // truncate the old automation before adding a replacement curve so the events never overlap.
      // Firefox does not implement cancelAndHoldAtTime, so emulate it with the older Web Audio
      // primitives there. Capture the live value before cancelling because cancellation may
      // restore the value from the beginning of the previous automation event.
      if (typeof parameter.cancelAndHoldAtTime === 'function') {
        parameter.cancelAndHoldAtTime(now);
      } else {
        parameter.cancelScheduledValues(now);
        parameter.setValueAtTime(start, now);
      }
      const end = track === target ? 1 : 0;
      const curve = new Float32Array(CROSSFADE_STEPS);
      for (let i = 0; i < curve.length; i++) {
        const t = i / (curve.length - 1);
        // Equal-power sin/cos curves keep the perceived loudness steady through the blend.
        const progress = end > start ? Math.sin(t * Math.PI / 2) : 1 - Math.cos(t * Math.PI / 2);
        curve[i] = start + (end - start) * progress;
      }
      parameter.setValueCurveAtTime(curve, now, CROSSFADE_SECONDS);
    }
    this.current = target;
  }

  private clearReleaseTimer(): void {
    if (this.releaseTimer === null) return;
    window.clearTimeout(this.releaseTimer);
    this.releaseTimer = null;
  }
}

/**
 * A base is threatened when an enemy squad crosses into its Core territory, or when a squad is
 * planted and fighting one of the castle-wall segments belonging to that base's ring.
 */
export function isAnyBaseUnderAttack(sim: Sim): boolean {
  const { state, map } = sim;
  const baseWallOwners = new Map<number, PlayerId>();
  for (const ruin of state.ruins) {
    if (!ruin.group?.startsWith('base-') || ruin.buildingId === null) continue;
    const wall = state.buildings.get(ruin.buildingId);
    if (wall && wall.state !== 'destroyed') baseWallOwners.set(wall.id, wall.owner);
  }

  for (const squad of state.squads.values()) {
    if (squad.dead) continue;
    for (const defender of sim.playerIds()) {
      if (sim.areHostile(squad.owner, defender) && dist(squad.pos, map.corePositions[defender]) <= map.coreTerritoryRadius) {
        return true;
      }
    }

    if (squad.targetId === null) continue;
    const wallOwner = baseWallOwners.get(squad.targetId);
    if (wallOwner === undefined || !sim.areHostile(wallOwner, squad.owner)) continue;
    if (squad.engaged || state.time - squad.lastCombatAt <= 0.75) return true;
  }

  return false;
}
